import { z } from 'zod';
import { ReachFeature } from './features.js';
import { voiceAgentNameSchema } from './voice-agent-settings.js';
export var ComplianceCategory;
(function (ComplianceCategory) {
    ComplianceCategory["MARKETING"] = "marketing";
    ComplianceCategory["TRANSACTIONAL"] = "transactional";
})(ComplianceCategory || (ComplianceCategory = {}));
export const complianceCategorySchema = z
    .nativeEnum(ComplianceCategory)
    .default(ComplianceCategory.MARKETING);
export const accountType = z.enum(['production', 'sandbox']);
// Management fee configuration
export const managementFeeMode = z.enum(['percentage', 'fixed']);
// Percentage value (e.g. 25 = 25%)
const managementFeePercentSchema = z.number().min(0).max(100);
// Fixed daily fee in cents
const managementFeeFixedCentsSchema = z.number().int().nonnegative();
// Daily budget bound in dollars
const budgetBoundSchema = z.number().int().min(1);
export const managementFeeSchema = z.object({
    // How the fee should be calculated
    mode: managementFeeMode.default('percentage'),
    percent: managementFeePercentSchema.optional().default(25),
    fixedCents: managementFeeFixedCentsSchema.optional(),
});
// Budget range configuration
export const budgetRangeSchema = z.object({
    min: budgetBoundSchema.optional().default(20),
    max: budgetBoundSchema.optional().default(100),
});
export var VoiceContactFieldType;
(function (VoiceContactFieldType) {
    VoiceContactFieldType["NAME"] = "name";
    VoiceContactFieldType["PHONE"] = "phone";
    VoiceContactFieldType["EMAIL"] = "email";
    VoiceContactFieldType["ADDRESS"] = "address";
})(VoiceContactFieldType || (VoiceContactFieldType = {}));
/**
 * Post-call extraction targets, and the allowlist a partner may push live context against.
 * Eligibility for the latter is decided in `@reach/shared-types/voice-active-call-context`.
 */
export const postCallAnalysisVariableSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('string'),
        name: z.string(),
        description: z.string(),
        contactField: z.nativeEnum(VoiceContactFieldType).optional(),
    }),
    z.object({
        type: z.literal('boolean'),
        name: z.string(),
        description: z.string(),
    }),
    z.object({
        type: z.literal('enum'),
        name: z.string(),
        description: z.string(),
        choices: z.array(z.string()),
    }),
]);
export const AUTOMATION_BUILDER_EXAMPLE_PROMPT_LIMIT = 4;
export const AUTOMATION_BUILDER_EXAMPLE_PROMPT_TITLE_MAX = 60;
export const AUTOMATION_BUILDER_EXAMPLE_PROMPT_BODY_MAX = 500;
export const automationBuilderExamplePromptSchema = z.object({
    title: z.string().trim().min(1).max(AUTOMATION_BUILDER_EXAMPLE_PROMPT_TITLE_MAX),
    prompt: z.string().trim().min(1).max(AUTOMATION_BUILDER_EXAMPLE_PROMPT_BODY_MAX),
});
/** Partner-configured example prompts shown as chips in the tenant AI Automation Builder, in display order. */
export const automationBuilderExamplePromptsSchema = z
    .array(automationBuilderExamplePromptSchema)
    .max(AUTOMATION_BUILDER_EXAMPLE_PROMPT_LIMIT);
const perMinuteUsageMarkupCentsSchema = z.number().nonnegative();
const aiAssistantNameSchema = z.string().max(60);
const aiAssistantIconUrlSchema = z.string().url();
export const featureCustomizationsSchema = z
    .object({
    acquire: z
        .object({
        // Optional overrides for management fee calculation in Acquire
        managementFee: managementFeeSchema,
        // Optional budget range configuration for Acquire ads builder
        budgetRange: budgetRangeSchema,
    })
        .optional(),
    measure: z
        .object({
        dashboardHeaderText: z.string().optional().default(''),
        dashboardHeaderDescription: z.string().optional().default(''),
        showPartnerAdsBanner: z.boolean().optional(),
        partnerAdsBannerText: z.string().optional().default(''),
    })
        .optional(),
    reputation: z
        .object({
        smsEnabled: z.boolean().default(false),
        complianceCategory: complianceCategorySchema,
        dashboardHeaderText: z.string().optional().default(''),
        dashboardHeaderDescription: z.string().optional().default(''),
    })
        .optional(),
    engage: z
        .object({
        /**
         * When true, partners may configure point-of-contact (POC) sender for Engage automations
         * (template + tenant toggles, schema mappings). Send-time still requires explicit opt-in per automation.
         */
        enablePointOfContactSender: z.boolean().optional(),
        showDeactivateEmailSmsButton: z.boolean().optional(),
        /** Unset means on. */
        onboardingAutomationsEnabledByDefault: z.boolean().optional(),
        automationBuilderExamplePrompts: automationBuilderExamplePromptsSchema.optional(),
        betaFeature: z
            .object({
            sms: z
                .object({
                enabled: z.boolean().default(false),
                bannerText: z.string().optional().default(''),
            })
                .optional(),
        })
            .optional(),
    })
        .optional(),
    // Feature configuration for hiding features
    hideFeature: z
        .object({
        [ReachFeature.ENGAGE]: z
            .object({
            sms: z.boolean().optional(),
            sales: z.boolean().optional(),
        })
            .optional(),
    })
        .optional(),
    // Feature configuration for enabling features
    enableFeature: z
        .object({
        [ReachFeature.ENGAGE]: z
            .object({
            onboarding: z.boolean().default(true),
        })
            .optional(),
    })
        .optional(),
    voice: z
        .object({
        agentName: voiceAgentNameSchema.optional(),
        allowTenantAgentNameOverride: z.boolean().optional(),
        dashboardHeaderText: z.string().optional().default(''),
        dashboardHeaderDescription: z.string().optional().default(''),
        informationCollectionPrompt: z.string().optional(),
        postCallAnalysisVariables: z.array(postCallAnalysisVariableSchema).optional(),
        /**
         * Partner-authored copy naming the actions this partner's voice agents take on a call.
         * Onboarding hides the row entirely when it is blank. The grants themselves live in
         * `agent_templates` / `voice_agents.tool_names` — this only drives what onboarding claims.
         */
        agentActionsDescription: z.string().optional(),
        billing: z
            .object({
            /**
             * Optional per-minute usage rate in cents for partner voice billing. Partner dashboards
             * bill `SUM(duration_secs) * perMinuteUsageMarkupCents / 60` (provider `call_cost` is not used).
             */
            perMinuteUsageMarkupCents: perMinuteUsageMarkupCentsSchema.optional(),
        })
            .optional(),
    })
        .optional(),
    sdk: z
        .object({
        allowViewportOverlay: z.boolean().default(false),
    })
        .optional(),
    /** Partner-chosen display name for the in-app AI assistant. Absent means the Reach default. */
    aiAssistantName: aiAssistantNameSchema.optional(),
    aiAssistantIconUrl: aiAssistantIconUrlSchema.optional(),
})
    .optional();
/** A key may be left out, or sent as `null` to remove it. */
const patchable = (schema) => schema.nullable().optional();
/**
 * No defaults, so validating a patch cannot write values the caller never sent. A type test fails
 * the build when this stops matching `FeatureCustomizationsPatch`.
 */
export const featureCustomizationsPatchSchema = z.object({
    acquire: patchable(z.object({
        managementFee: patchable(z.object({
            mode: patchable(managementFeeMode),
            percent: patchable(managementFeePercentSchema),
            fixedCents: patchable(managementFeeFixedCentsSchema),
        })),
        budgetRange: patchable(z.object({
            min: patchable(budgetBoundSchema),
            max: patchable(budgetBoundSchema),
        })),
    })),
    measure: patchable(z.object({
        dashboardHeaderText: patchable(z.string()),
        dashboardHeaderDescription: patchable(z.string()),
        showPartnerAdsBanner: patchable(z.boolean()),
        partnerAdsBannerText: patchable(z.string()),
    })),
    reputation: patchable(z.object({
        smsEnabled: patchable(z.boolean()),
        complianceCategory: patchable(z.nativeEnum(ComplianceCategory)),
        dashboardHeaderText: patchable(z.string()),
        dashboardHeaderDescription: patchable(z.string()),
    })),
    engage: patchable(z.object({
        enablePointOfContactSender: patchable(z.boolean()),
        showDeactivateEmailSmsButton: patchable(z.boolean()),
        onboardingAutomationsEnabledByDefault: patchable(z.boolean()),
        automationBuilderExamplePrompts: patchable(automationBuilderExamplePromptsSchema),
        betaFeature: patchable(z.object({
            sms: patchable(z.object({
                enabled: patchable(z.boolean()),
                bannerText: patchable(z.string()),
            })),
        })),
    })),
    hideFeature: patchable(z.object({
        [ReachFeature.ENGAGE]: patchable(z.object({
            sms: patchable(z.boolean()),
            sales: patchable(z.boolean()),
        })),
    })),
    enableFeature: patchable(z.object({
        [ReachFeature.ENGAGE]: patchable(z.object({
            onboarding: patchable(z.boolean()),
        })),
    })),
    voice: patchable(z.object({
        agentName: patchable(voiceAgentNameSchema),
        allowTenantAgentNameOverride: patchable(z.boolean()),
        dashboardHeaderText: patchable(z.string()),
        dashboardHeaderDescription: patchable(z.string()),
        informationCollectionPrompt: patchable(z.string()),
        postCallAnalysisVariables: patchable(z.array(postCallAnalysisVariableSchema)),
        agentActionsDescription: patchable(z.string()),
        billing: patchable(z.object({
            perMinuteUsageMarkupCents: patchable(perMinuteUsageMarkupCentsSchema),
        })),
    })),
    sdk: patchable(z.object({
        allowViewportOverlay: patchable(z.boolean()),
    })),
    aiAssistantName: patchable(aiAssistantNameSchema),
    aiAssistantIconUrl: patchable(aiAssistantIconUrlSchema),
});
/**
 * Creates the default feature customizations for a new platform.
 * This is more flexible than SQL defaults as it can be easily updated
 * without requiring database migrations.
 */
export function createDefaultFeatureCustomizations() {
    return {
        acquire: {
            managementFee: {
                mode: 'percentage',
                percent: 25,
            },
            budgetRange: {
                min: 20,
                max: 100,
            },
        },
        measure: {
            dashboardHeaderText: '',
            dashboardHeaderDescription: '',
            showPartnerAdsBanner: false,
            partnerAdsBannerText: '',
        },
        reputation: {
            smsEnabled: false,
            complianceCategory: ComplianceCategory.MARKETING,
            dashboardHeaderText: '',
            dashboardHeaderDescription: '',
        },
        engage: {
            showDeactivateEmailSmsButton: true,
            betaFeature: {
                sms: {
                    enabled: false,
                    bannerText: '',
                },
            },
        },
        sdk: {
            allowViewportOverlay: true,
        },
    };
}
//# sourceMappingURL=platforms.js.map